import React, { useState, useEffect, useContext, useMemo } from 'react';
import Moment from "moment";
import { extendMoment } from "moment-range";
import db from './Firebase';
import ExportTableToExcel from './ExportTableToExcel';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import GlobalContext from './context/GlobalContext';
import { handleLogout } from './Utility';

// 👇 Hub ka naam subscription document se yahan se aata hai.
// Agar tumhare Firestore me field ka naam alag hai (e.g. "hub_name", "hub", "hub_id"),
// bas yahan change kar do.
// "null", "undefined", "" jaisi values ko missing maana jayega
const isValidHub = (v) =>
  v !== null &&
  v !== undefined &&
  typeof v !== 'object' &&
  !['', 'null', 'undefined', 'unknown', 'n/a', 'na'].includes(String(v).trim().toLowerCase());

// Kisi bhi object me hub dhoondhta hai: pehle known field names, phir koi bhi key jisme "hub" ho
const findHubInObject = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  const preferred = ['hub_name', 'hubName', 'hub', 'hub_id', 'hubId', 'hub_title'];
  for (const k of preferred) {
    if (isValidHub(obj[k])) return String(obj[k]).trim();
  }
  for (const k of Object.keys(obj)) {
    if (/hub/i.test(k) && isValidHub(obj[k])) return String(obj[k]).trim();
  }
  return null;
};

const getHub = (subscription) => findHubInObject(subscription) || 'Unknown';

// Customer document (customers_data) se hub nikalne ke liye.
// Pehle seedha customer ke fields check hote hain, phir address ke andar.
// Field ka naam alag ho to yahan add kar do.
const getHubFromCustomer = (customer) => {
  if (!customer) return null;

  // 1) customer ke top-level fields
  const direct = findHubInObject(customer);
  if (direct) return direct;

  // 2) customer ke andar jo bhi object / array fields hain (address, addresses, etc.)
  for (const value of Object.values(customer)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = findHubInObject(item);
        if (found) return found;
      }
    } else if (value && typeof value === 'object') {
      const found = findHubInObject(value);
      if (found) return found;
    }
  }
  return null;
};

// Jin subscriptions me hub nahi mila, unka hub customers_data se nikalta hai.
// Customer ko 3 tareeke se dhoondhta hai: customer_id field, document ID, aur phone.
// Return: Map(key -> hub), key = doc ID / customer_id / customer_phone (string)
const fetchCustomerHubMap = async (unknownSubs) => {
  const map = new Map();
  const clean = (v) => (v === undefined || v === null || String(v).trim() === '' ? null : String(v).trim());

  const addDoc = (docId, customer) => {
    const hub = getHubFromCustomer(customer);
    if (!hub) return;
    [docId, customer.customer_id, customer.customer_phone].forEach(k => {
      const key = clean(k);
      if (key) map.set(key, hub);
    });
  };

  const ids = [...new Set(unknownSubs.map(s => clean(s.customer_id)).filter(Boolean))];
  const phones = [...new Set(unknownSubs.map(s => clean(s.customer_phone || s.phone)).filter(Boolean))];

  // 'in' query: max 10 values, string + number dono variants try karte hain
  const runIn = async (field, values) => {
    const variants = [];
    values.forEach(v => {
      variants.push(v);
      const n = Number(v);
      if (!Number.isNaN(n) && String(n) === v) variants.push(n);
    });
    const chunks = [];
    for (let i = 0; i < variants.length; i += 10) chunks.push(variants.slice(i, i + 10));
    const snaps = await Promise.all(
      chunks.map(c => db.collection('customers_data').where(field, 'in', c).get())
    );
    snaps.forEach(snap => snap.forEach(d => addDoc(d.id, d.data())));
  };

  const byDocId = Promise.all(
    ids.map(async id => {
      try {
        const d = await db.collection('customers_data').doc(id).get();
        if (d.exists) addDoc(d.id, d.data());
      } catch (e) { /* invalid doc id, ignore */ }
    })
  );

  await Promise.all([
    ids.length ? runIn('customer_id', ids) : null,
    phones.length ? runIn('customer_phone', phones) : null,
    byDocId
  ]);

  const unresolved = unknownSubs
    .filter(s => ![s.customer_id, s.customer_phone, s.phone].some(k => clean(k) && map.has(clean(k))))
    .map(s => ({ customer_id: s.customer_id, customer_phone: s.customer_phone || s.phone || null }));
  if (unresolved.length > 0) {
    console.warn('customers_data me in customers ka hub/document nahi mila:', unresolved);
  }
  return map;
};

const KEY_SEP = '||';

function PredictiveAnalysis() {
  const { permissible_roles } = useContext(GlobalContext);

  const navigate = useNavigate();

  useEffect(() => {
    const loggedIn = localStorage.getItem("loggedIn") === "true";
    console.log(loggedIn);
    if (!loggedIn) {
      navigate("/login");
    } else {
      if (permissible_roles.length > 0) {
        if (!permissible_roles.includes('predictive_analysis')) {
          handleLogout();
          navigate("/permission_denied");
        }
      }
    }
  }, [navigate, permissible_roles]);

  const moment = extendMoment(Moment);
  const [cummulativeHubDeliveryList, setCummulativeHubDeliveryList] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [selectedHub, setSelectedHub] = useState('All');

  const [calendarMap, setCalendarMap] = useState(new Map());

  // Dropdown ke liye unique hubs
  const hubOptions = useMemo(() => {
    const hubs = new Set(cummulativeHubDeliveryList.map(item => item.hub));
    return ['All', ...Array.from(hubs).sort()];
  }, [cummulativeHubDeliveryList]);

  // Selected hub ke hisaab se filtered list
  const filteredList = useMemo(() => {
    if (selectedHub === 'All') return cummulativeHubDeliveryList;
    return cummulativeHubDeliveryList.filter(item => item.hub === selectedHub);
  }, [cummulativeHubDeliveryList, selectedHub]);

  const totalQuantity = useMemo(
    () => filteredList.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    [filteredList]
  );

  const fetchBulkQty = async () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const bulk_calendar_snap = await db.collection('bulk_update_quantity')
      .where('delivery_date', '==', moment(tomorrow).format('YYYY-MM-DD'))
      .get();

    const calendar_data = bulk_calendar_snap.docs.map(doc => doc.data());
    const newMap = new Map();
    calendar_data.forEach(data => {
      const value = data.quantity;
      newMap.set(data.subscription_id, value);
    });
    console.log("New Map => ", newMap);
    setCalendarMap(newMap);
    return newMap;
  };

  const checkVacationStatus = async () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const endOfTomorrow = new Date(tomorrow);
    endOfTomorrow.setHours(23, 59, 59, 999);

    try {
      const vacationQuerySnapshot = await db.collection('customers_vacation')
        .where('start_date', '>=', tomorrow)
        .where('end_date', '<=', endOfTomorrow)
        .get();

      const vacation_data = vacationQuerySnapshot.docs.map(doc => doc.data());
      const newMap = new Map();
      vacation_data.forEach(data => {
        newMap.set(data.customer_id, true);
      });
      return newMap;
    } catch (error) {
      console.error('Error fetching vacation data:', error);
      return new Map();
    }
  };

  const calculatePredictAnalysisQty = async () => {
    setLoading(true);
    let bulk_Quantity_map = new Map();
    let vacation_map = new Map();
    const customSubscriptionList = [];
    const nonCustomSubscriptionList = [];
    bulk_Quantity_map = await fetchBulkQty();
    vacation_map = await checkVacationStatus();
    console.log("vacation map size =>", vacation_map.size);

    try {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayOfWeekIndex = tomorrow.getDay();
      const dayOfWeek = weekdays[dayOfWeekIndex];
      console.log("day of week", dayOfWeek);

      const customSnapshot = await db.collection('subscriptions_data')
        .where(dayOfWeek, '>=', 1)
        .where('subscription_type', '==', 'Custom')
        .where('status', '==', '1')
        .get();

      customSnapshot.forEach(doc => {
        const subscription = doc.data();
        if (!vacation_map.has(subscription.customer_id)) {
          if (bulk_Quantity_map.has(subscription.subscription_id)) {
            subscription.quantity = bulk_Quantity_map.get(subscription.subscription_id);
          } else {
            subscription.quantity = subscription[dayOfWeek];
          }
          customSubscriptionList.push(subscription);
        }
      });

      const nonCustomSubSnapshot = await db.collection('subscriptions_data')
        .where('subscription_type', '!=', 'Custom')
        .where('status', '==', '1')
        .where('next_delivery_date', '==', moment(tomorrow).format('YYYY-MM-DD'))
        .get();

      nonCustomSubSnapshot.forEach(doc => {
        const subscription = doc.data();
        if (!vacation_map.has(subscription.customer_id)) {
          if (bulk_Quantity_map.has(subscription.subscription_id)) {
            subscription.quantity = bulk_Quantity_map.get(subscription.subscription_id);
            if (typeof bulk_Quantity_map.get(subscription.subscription_id) == 'string') {
              console.log(subscription);
            }
          }
          nonCustomSubscriptionList.push(subscription);
        }
      });

      const combinedSubscriptionList = [...customSubscriptionList, ...nonCustomSubscriptionList];
      console.log(combinedSubscriptionList);

      // Jin subscriptions me hub nahi mila, unka hub customers_data (address) se fetch karo
      const unknownSubs = combinedSubscriptionList.filter(sub => getHub(sub) === 'Unknown');

      let customerHubMap = new Map();
      if (unknownSubs.length > 0) {
        try {
          customerHubMap = await fetchCustomerHubMap(unknownSubs);
        } catch (err) {
          console.error('Error fetching customer hubs:', err);
        }
      }

      const resolveHub = (sub) => {
        const hub = getHub(sub);
        if (hub !== 'Unknown') return hub;
        const byId = sub.customer_id != null ? customerHubMap.get(String(sub.customer_id).trim()) : null;
        const phone = sub.customer_phone || sub.phone;
        const byPhone = phone != null ? customerHubMap.get(String(phone).trim()) : null;
        return byId || byPhone || 'Unknown';
      };

      combinedSubscriptionList
  .filter(sub => resolveHub(sub) === 'Unknown')
  .forEach(sub => console.log('STILL UNKNOWN:', sub));

      // Ab key me hub bhi hai -> product/packaging/price + hub ke hisaab se group hoga
      const productPackagingMap = new Map();

      combinedSubscriptionList.forEach(subscription => {
        const hub = resolveHub(subscription);
        const qty = Number(subscription.quantity || 0);
        const key = [
          hub,
          subscription.product_name,
          subscription.package_unit,
          subscription.price
        ].join(KEY_SEP);

        productPackagingMap.set(key, (productPackagingMap.get(key) || 0) + qty);
      });

      const a2Predictive = combinedSubscriptionList
        .filter(sub => sub.product_name === "A2 Cow's Milk")
        .map(sub => ({
          subscription_id: sub.subscription_id,
          customer_id: sub.customer_id,
          hub: resolveHub(sub),
          quantity: Number(sub.quantity || 0),
          package_unit: sub.package_unit,
          price: sub.price,
          subscription_type: sub.subscription_type
        }));

      window.predictiveA2 = a2Predictive;
      console.log("PREDICTIVE A2 COUNT:", a2Predictive.length);
      console.log("PREDICTIVE A2 TOTAL:", a2Predictive.reduce((sum, x) => sum + x.quantity, 0));

      const cumulativeList = [];
      for (const [key, quantity] of productPackagingMap.entries()) {
        const [hub, productName, packageUnit, price] = key.split(KEY_SEP);
        cumulativeList.push({
          hub: hub,
          product_name: productName,
          packaging: packageUnit,
          unit_price: parseFloat(price),
          quantity: quantity,
          analysisDate: moment(tomorrow).format('DD-MM-YYYY'),
          day: dayOfWeek
        });
      }

      // Hub -> Product ke hisaab se sort
      cumulativeList.sort((a, b) =>
        a.hub.localeCompare(b.hub) || a.product_name.localeCompare(b.product_name)
      );

      setCummulativeHubDeliveryList(cumulativeList);
      console.log(cumulativeList);
      setDataLoaded(true);
      createFileName();

    } catch (error) {
      console.error("Error calculating cumulative report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    calculatePredictAnalysisQty();
  }, []);

  const createFileName = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const startDateString = moment(tomorrow).format('DD-MM-YYYY');
    setFileName(`Hub_deliveries_${startDateString}`);
  };

  // File name me selected hub bhi aa jaye
  const exportFileName = selectedHub === 'All'
    ? fileName
    : `${fileName}_${selectedHub.replace(/\s+/g, '_')}`;

  /** export pdf */
  const exportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = [
      "SR.NO",
      "Hub",
      "Date",
      "Day",
      "Product",
      "Packaging",
      "Unit Price",
      "Total Quantity",
    ];
    const tableRows = filteredList.map((item, index) => [
      index + 1,
      item.hub,
      item.analysisDate,
      item.day,
      item.product_name,
      item.packaging,
      item.unit_price,
      item.quantity
    ]);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
    });

    doc.save(`PredictiveReport${selectedHub === 'All' ? '' : '_' + selectedHub.replace(/\s+/g, '_')}.pdf`);
  };

  const exportToCSV = () => {
    const csvRows = [];
    csvRows.push(['Hub', 'Date', 'Day', 'Product', 'Packaging', 'Unit Price', 'Total Quantity'].join(','));

    for (const delivery of filteredList) {
      const row = [
        `"${delivery.hub}"`,
        delivery.analysisDate,
        delivery.day,
        `"${delivery.product_name}"`,
        delivery.packaging,
        delivery.unit_price,
        delivery.quantity
      ].join(',');
      csvRows.push(row);
    }

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `${exportFileName}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <>
      {loading && (
        <div className="loader-overlay">
          <div className="">
            <img style={{
              height: "6rem"
            }} src="images/loader.gif" alt=""></img>
          </div>
        </div>
      )}
      <div className="container-scroller">
        <div className="container-fluid">
          <div className="main-panel" style={{ width: '100%' }}>
            <div className='panel' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: "18px", color: "#288a84", fontWeight: "700", marginTop: "12px" }}>Predictive Analysis</span>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {/* Hub filter */}
                <select
                  className="form-control form-control-sm mt-1"
                  style={{ width: '200px' }}
                  value={selectedHub}
                  onChange={(e) => setSelectedHub(e.target.value)}
                >
                  {hubOptions.map(hub => (
                    <option key={hub} value={hub}>
                      {hub === 'All' ? 'All Hubs' : hub}
                    </option>
                  ))}
                </select>

                {dataLoaded && (
                  <ExportTableToExcel
                    tableId="predictive_analysis"
                    fileName={exportFileName}
                  />
                )}
                <button className="btn btn-success btn-rounded btn-sm mt-1" onClick={exportToPDF}>Export to PDF</button>
                <button className="btn btn-success btn-rounded btn-sm mt-1" onClick={exportToCSV}>Export to CSV</button>
              </div>
            </div>

            <div style={{ margin: '10px 0', fontWeight: 600 }}>
              {selectedHub === 'All' ? 'All Hubs' : selectedHub} — Total Quantity: {totalQuantity}
            </div>

            <table className="table table-striped" id="predictive_analysis">
              <thead>
                <tr>
                  <th>Hub</th>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Product</th>
                  <th>Packaging</th>
                  <th>Unit Price</th>
                  <th>Total Quantity</th>
                </tr>
              </thead>
              <tbody>
                {dataLoaded &&
                  filteredList.map((delivery, index) => (
                    <tr key={index}>
                      <td>{delivery.hub}</td>
                      <td>{delivery.analysisDate}</td>
                      <td>{delivery.day}</td>
                      <td>{delivery.product_name}</td>
                      <td>{delivery.packaging}</td>
                      <td>{delivery.unit_price}</td>
                      <td>{delivery.quantity}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default PredictiveAnalysis;